import { observer } from "mobx-react";
import React, { useImperativeHandle, useState } from "react";
import { useStore } from "../../../stores/store";
import Modal from "../../../custom-components/model";
import { Button, Grid, Group, TextInput, Text } from "@mantine/core";
import t from "../../../stores/language";
import { useStyles } from "../configure-input";
import { Report } from "../../../models/models";
import theme from "../../../stores/theme";
import { delay } from "../module-assumptions";
export interface ReportSaveHandle {
  show: (report: Report) => void;
}

export interface ReportSaveProps {
  closeAndReset?: {
    closeMainModal: () => void;
    resetReport: () => void;
    resetSelectedLookupYear: () => void;
    resetCalculatedFormulaResult: () => void;
  };
}

export const defaultReport: Report = {
  id: 0,
  name: "",
  moduleId: 0,
  selectedInputs: null,
  formulasResult: {
    detailedResult: [],
    calculatedResult: [],
  },
  createdDate: new Date(),
  createdBy: 0,
};

const ReportSave = observer(
  React.forwardRef<ReportSaveHandle, ReportSaveProps>(function ReportSave({ closeAndReset }, ref) {
    const { store } = useStore();
    const { classes } = useStyles();
    const [error, setError] = useState<{ isError: boolean; message: string }>({ isError: false, message: "" });
    const [visible, setVisible] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [report, setReport] = useState<Report>(defaultReport);

    useImperativeHandle(ref, () => {
      return {
        show: show,
      };
    });

    const show = (reportData: Report) => {
      setReport(reportData);
      setVisible(true);
    };

    const resetState = () => {
      setVisible(false);
      setError({ isError: false, message: "" });
    };

    const saveAsReport = async () => {
      if (report.name === "") {
        setError({ isError: true, message: t("Please Enter Report Name") });
        return;
      }
      setSaving(true);
      const result = await store.SaveAsReport(report);
      if (result.error) {
        setError({ isError: true, message: result.message as string });
        setSaving(false);
        return;
      } else {
        setError({ isError: false, message: t("Saved Successfully") });
        await delay(400);
        setSaving(false);

        // Outer Modal Close
        if (closeAndReset) {
          closeAndReset.closeMainModal();
          closeAndReset.resetReport();
          closeAndReset.resetSelectedLookupYear();
          closeAndReset.resetCalculatedFormulaResult();
        }
      }
      resetState();
    };

    return (
      <>
        <Modal
          opened={visible}
          header={t("Save As Report")}
          footer={
            <Group className={classes.modalFooter}>
              <Button
                variant={"default"}
                onClick={() => {
                  resetState();
                }}
              >
                {t("Cancel")}
              </Button>
              <Button onClick={() => saveAsReport()} loading={saving}>
                {t("Save")}
              </Button>
            </Group>
          }
          withCloseButton={true}
          onClose={() => {
            resetState();
          }}
          size={"35%"}
        >
          <Grid pb={15} pl={15} pr={15} columns={12} m={0}>
            <Grid.Col span={12}>
              <TextInput
                label={t("Name")}
                radius={0}
                value={report?.name ?? ""}
                onChange={(e) => {
                  if (e.target.value !== "" && error.isError) {
                    setError({ isError: false, message: "" });
                  }
                  setReport({ ...report, name: e.target.value ?? "" });
                }}
              />
            </Grid.Col>
            {error.message !== "" ? (
              <Grid.Col span={12}>
                <Text size={theme.fontSizes?.sm} color={error.isError ? "red" : "green"}>
                  {error.message}
                </Text>
              </Grid.Col>
            ) : null}
          </Grid>
        </Modal>
      </>
    );
  })
);

export default ReportSave;
